// The sixteen canonical strings. Kit 07 item 165.
//
// "Sixteen canonical strings, verbatim. They are in Kit 07 item 165. Do not
// paraphrase them per screen."
//
// A rule like that cannot live in prose. The moment two components each hold
// their own copy of "Not measured", one of them becomes "Not measured yet" in
// a hurry and nothing catches it — the strings are identical in a diff only
// while they are identical.
//
// So they live here, exported by name, and every component imports the name
// rather than typing the words. Phase 3 opened the file with the two F4 needed;
// Phase 5 filled in the rest against item 165, which is where the list is
// specified. `npm run kit:check` reads the sixteen out of the specimen and
// compares them to CANONICAL below, so a drift in either direction fails.
//
// The `state` each one belongs to is item 165's own first column, kept because
// it is the difference between a string list and a vocabulary: "Measuring" is
// the RUN IN PROGRESS string, and using it for a loading skeleton would be a
// paraphrase even though the characters match.

/** One canonical string, with the state item 165 files it under. */
export interface CanonicalString {
  state: string;
  text: string;
}

/* ── the sixteen, in the specimen's own order ────────────────────────────── */

/** F4, and the most-used of the sixteen. Never 0%, never a dash, never empty. */
export const NOT_MEASURED = "Not measured";

/** F4. What a delta of exactly zero says. Never "0.0". */
export const NO_MOVEMENT = "No movement";

export const NO_ANALYSIS_YET = "No analysis yet";
export const EMPTY_BODY = "AI already has an opinion. Start shaping it here.";
export const START_ANALYSIS = "Start analysis";
export const GENERATING = "Writing an action for this gap";
export const WARMING = "Computing this attribute";
export const WARMING_META = "Takes 15 to 45 seconds";
export const LOCKED = "Not included in this plan";
export const PARTIAL_ERROR = "Two models did not respond";
export const PARTIAL_BODY = "Partial result saved.";
export const PARTIAL_ACTION = "Retry those two";
export const CAP_REACHED = "Three custom attributes per run";
export const RUN_IN_PROGRESS = "Measuring";
export const RUN_META = "Three of four models returned";
export const LAST_RUN = "Last run 4 August 2026";

/**
 * The list, for the gate.
 *
 * Order and wording are item 165's, read off the specimen. `LAST_RUN` carries
 * a date, which is the one entry that is a TEMPLATE rather than a constant in
 * production — the string is canonical, the date inside it is not, and a
 * screen renders it through `lastRun()` below rather than by editing the words.
 */
export const CANONICAL: CanonicalString[] = [
  { state: "empty", text: NO_ANALYSIS_YET },
  { state: "body", text: EMPTY_BODY },
  { state: "action", text: START_ANALYSIS },
  { state: "unmeasured", text: NOT_MEASURED },
  { state: "generating", text: GENERATING },
  { state: "warming", text: WARMING },
  { state: "meta", text: WARMING_META },
  { state: "locked", text: LOCKED },
  { state: "error", text: PARTIAL_ERROR },
  { state: "body", text: PARTIAL_BODY },
  { state: "action", text: PARTIAL_ACTION },
  { state: "cap reached", text: CAP_REACHED },
  { state: "run in progress", text: RUN_IN_PROGRESS },
  { state: "meta", text: RUN_META },
  { state: "no movement", text: NO_MOVEMENT },
  { state: "meta", text: LAST_RUN },
];

/**
 * "Last run 4 August 2026" with a real date in it.
 *
 * The specimen's entry has a date baked in, which cannot be a constant in a
 * product. So the WORDS are canonical and the date is a parameter: a screen
 * that wanted "Last measured 4 August 2026" would be paraphrasing, and this
 * function is what makes that a visible edit rather than a quiet one.
 */
export function lastRun(formatted: string): string {
  return `Last run ${formatted}`;
}

/**
 * "Three of four models returned" with real counts.
 *
 * Same reasoning, and the numbers are words rather than digits because the
 * specimen writes them that way: this is meta prose, and F4's rule is that
 * prose uses proportional figures.
 */
const WORD = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
export function modelsReturned(returned: number, total: number): string {
  const n = (v: number) => WORD[v] ?? String(v);
  const capitalised = n(returned).charAt(0).toUpperCase() + n(returned).slice(1);
  return `${capitalised} of ${n(total)} models returned`;
}
